// src/pages/Home.tsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import GameCard from "../components/GameCard";
import FasoorModal from "../components/FasoorModal";
import Footer from "../components/Footer";
import SplashScreen from "../components/SplashScreen";
import NotificationBell from "../components/NotificationBell";

import pArenaLogo from "../assets/images/logos/p-arena-logo.png";

import {
  logoutSocket,
} from "../socket";

import API_URL from "../config/api";

import "../components/Home.css";
import "../styles/global.css";

// =====================================================
// TYPES
// =====================================================

interface Game {
  id: number;
  name: string;
  image: string;
  available: boolean;

  rating?: {
    averageRating: number;
    ratingCount: number;
  };
}

interface CurrentUser {
  id: number | string;
  username: string;
  full_name: string;
  email: string;
  avatar: string | null;
  bio?: string | null;
  rating?: number;
  role?: "user" | "admin";
}

interface GameRatingSummary {
  gameKey: string;
  averageRating: number;
  ratingCount: number;

  distribution: {
    "1": number;
    "2": number;
    "3": number;
    "4": number;
    "5": number;
  };
}

// =====================================================
// GAMES
// =====================================================

const ALL_GAMES: Game[] = [
  {
    id: 1,
    name: "Fasoor",
    image: "/games/fasoor.png",
    available: true,
  },
  {
    id: 2,
    name: "Tika",
    image: "/games/tika.png",
    available: false,
  },
  {
    id: 3,
    name: "Bitruno",
    image: "/games/bitruno.png",
    available: false,
  },
  {
    id: 4,
    name: "Charwali",
    image: "/games/charwali.png",
    available: false,
  },
  {
    id: 5,
    name: "Fiskoot",
    image: "/games/fiskoot.png",
    available: false,
  },
];

// =====================================================
// AVATAR URL
// =====================================================

const getAvatarUrl = (
  avatarPath: string | null | undefined
) => {

  if (!avatarPath) {
    return "/avatars/user.png";
  }

  // Uploaded backend avatar
  if (
    avatarPath.startsWith(
      "/uploads/"
    )
  ) {
    return `${API_URL}${avatarPath}`;
  }

  // Built-in frontend avatar
  return avatarPath;
};

// =====================================================
// HOME
// =====================================================

const Home: React.FC = () => {

  // ===================================================
  // ROUTER
  // ===================================================

  const navigate =
    useNavigate();

  // ===================================================
  // STATE
  // ===================================================

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    games,
    setGames,
  ] = useState<Game[]>([]);

  const [
    currentUser,
    setCurrentUser,
  ] = useState<CurrentUser | null>(
    null
  );

  const [
    showLogoutConfirm,
    setShowLogoutConfirm,
  ] = useState(false);

  const [
    loadingGame,
    setLoadingGame,
  ] = useState<string | null>(
    null
  );

  // ===================================================
  // P-ARENA OVERALL COMMUNITY RATING
  // ===================================================

  const [
    overallRating,
    setOverallRating,
  ] = useState<GameRatingSummary | null>(
    null
  );

  const [
    ratingLoading,
    setRatingLoading,
  ] = useState(true);

  // ===================================================
  // INDIVIDUAL GAME RATINGS
  // ===================================================

  const [
    gameRatings,
    setGameRatings,
  ] = useState<
    Record<
      string,
      {
        averageRating: number;
        ratingCount: number;
      }
    >
  >({});

  // ===================================================
  // AUTH STATUS
  // ===================================================

  const isLoggedIn =
    Boolean(
      localStorage.getItem(
        "token"
      )
    );

  // ===================================================
  // LOAD FEATURED GAMES
  // ===================================================

  useEffect(() => {

    /*
      Fasoor is currently the main playable game.

      Always keep it first in Featured Games.

      Randomize only the Coming Soon games.
    */

    const fasoor =
      ALL_GAMES.find(
        (game) =>
          game.name === "Fasoor"
      );

    const comingSoonGames =
      ALL_GAMES
        .filter(
          (game) =>
            game.name !== "Fasoor"
        )
        .sort(
          () =>
            Math.random() - 0.5
        )
        .slice(
          0,
          3
        );

    if (fasoor) {

      setGames([
        fasoor,
        ...comingSoonGames,
      ]);

    } else {

      setGames(
        comingSoonGames
      );

    }

  }, []);

  // ===================================================
  // LOAD CURRENT USER
  // ===================================================

  useEffect(() => {

    const loadCurrentUser =
      async () => {

        const token =
          localStorage.getItem(
            "token"
          );

        if (!token) {

          setCurrentUser(
            null
          );

          return;
        }

        try {

          const response =
            await fetch(
              `${API_URL}/api/profile/me`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`,
                },
              }
            );

          const data =
            await response.json();

          if (!response.ok) {

            throw new Error(
              data.message ||
              "Unable to load profile."
            );
          }

          setCurrentUser(
            data.profile
          );

        } catch (error) {

          console.error(
            "Could not load current user profile:",
            error
          );

          setCurrentUser(
            null
          );
        }
      };

    loadCurrentUser();

  }, []);

  // ===================================================
  // LOAD P-ARENA OVERALL COMMUNITY RATING
  // ===================================================

  useEffect(() => {

    let cancelled =
      false;

    const loadOverallRating =
      async () => {

        try {

          setRatingLoading(
            true
          );

          const response =
            await fetch(
              `${API_URL}/api/games/ratings/overall`
            );

          const data =
            await response.json();

          if (!response.ok) {

            throw new Error(
              data.message ||
              "Unable to load P-ARENA rating."
            );
          }

          if (cancelled) {
            return;
          }

          setOverallRating({
            gameKey:
              "overall",

            averageRating:
              Number(
                data.averageRating ??
                0
              ),

            ratingCount:
              Number(
                data.ratingCount ??
                0
              ),

            distribution: {
              "1":
                Number(
                  data.distribution?.["1"] ??
                  0
                ),

              "2":
                Number(
                  data.distribution?.["2"] ??
                  0
                ),

              "3":
                Number(
                  data.distribution?.["3"] ??
                  0
                ),

              "4":
                Number(
                  data.distribution?.["4"] ??
                  0
                ),

              "5":
                Number(
                  data.distribution?.["5"] ??
                  0
                ),
            },
          });

        } catch (error) {

          console.error(
            "Could not load P-ARENA overall rating:",
            error
          );

          if (!cancelled) {

            setOverallRating(
              null
            );
          }

        } finally {

          if (!cancelled) {

            setRatingLoading(
              false
            );
          }
        }
      };

    loadOverallRating();

    return () => {

      cancelled =
        true;
    };

  }, []);

  // ===================================================
  // LOAD INDIVIDUAL GAME RATINGS
  // ===================================================

  useEffect(() => {

    let cancelled =
      false;

    const loadGameRatings =
      async () => {

        /*
          Only load ratings for playable games.

          When another game becomes available,
          setting available: true is enough for
          its rating to start loading.
        */

        const availableGames =
          ALL_GAMES.filter(
            (game) =>
              game.available
          );

        try {

          const results =
            await Promise.all(

              availableGames.map(
                async (game) => {

                  const gameKey =
                    game.name
                      .trim()
                      .toLowerCase()
                      .replace(
                        /\s+/g,
                        "-"
                      );

                  const response =
                    await fetch(
                      `${API_URL}/api/games/${gameKey}/rating`
                    );

                  const data =
                    await response.json();

                  if (!response.ok) {

                    throw new Error(
                      data.message ||
                      `Unable to load ${game.name} rating.`
                    );
                  }

                  return {
                    gameKey,

                    averageRating:
                      Number(
                        data.averageRating ??
                        0
                      ),

                    ratingCount:
                      Number(
                        data.ratingCount ??
                        0
                      ),
                  };
                }
              )
            );

          if (cancelled) {
            return;
          }

          const nextRatings:
            Record<
              string,
              {
                averageRating: number;
                ratingCount: number;
              }
            > = {};

          results.forEach(
            (result) => {

              nextRatings[
                result.gameKey
              ] = {
                averageRating:
                  result.averageRating,

                ratingCount:
                  result.ratingCount,
              };
            }
          );

          setGameRatings(
            nextRatings
          );

        } catch (error) {

          console.error(
            "Could not load individual game ratings:",
            error
          );
        }
      };

    loadGameRatings();

    return () => {

      cancelled =
        true;
    };

  }, []);

  // ===================================================
  // SEARCH RESULTS
  // ===================================================

  const filteredGames =
    useMemo(
      () => {

        const normalizedSearch =
          search
            .trim()
            .toLowerCase();

        if (!normalizedSearch) {
          return ALL_GAMES;
        }

        return ALL_GAMES.filter(
          (game) =>
            game.name
              .toLowerCase()
              .includes(
                normalizedSearch
              )
        );

      },
      [search]
    );

  // ===================================================
  // DISPLAYED GAMES
  // ===================================================

  const displayedGames =
    search.trim()
      ? filteredGames
      : games;

  // ===================================================
  // LOGOUT
  // ===================================================

  const openLogoutConfirm =
    () => {

      setShowLogoutConfirm(
        true
      );
    };

  const cancelLogout =
    () => {

      setShowLogoutConfirm(
        false
      );
    };

  const confirmLogout =
    () => {

      setShowLogoutConfirm(
        false
      );

      // Remove authentication
      localStorage.removeItem(
        "token"
      );

      localStorage.removeItem(
        "user"
      );

      // Remove authenticated socket
      logoutSocket();

      // Clear profile UI
      setCurrentUser(
        null
      );

      // Return home
      navigate("/");
    };

  // ===================================================
  // OPEN LOBBY
  // ===================================================

  const openLobby =
    () => {

      /*
        Lobby is public.

        Guests can enter the lobby and play with AI.

        Authentication is required inside Lobby.tsx
        only when the player selects a multiplayer mode.
      */

      navigate(
        "/lobby",
        {
          state: {
            selectedGame:
              "Fasoor",
          },
        }
      );
    };

  // ===================================================
  // OPEN GAME
  // ===================================================

  const openGame = (
    game: Game
  ) => {

    // -------------------------------------------------
    // COMING SOON
    // -------------------------------------------------

    /*
      GameCard handles the Coming Soon modal.

      Keep this guard as an additional safety check
      so unavailable games cannot enter the lobby.
    */

    if (!game.available) {
      return;
    }

    // -------------------------------------------------
    // GUEST ACCESS
    // -------------------------------------------------

    /*
      IMPORTANT:

      Do NOT check isLoggedIn here.

      Guest users are allowed to enter Fasoor because
      they can play against AI.

      Lobby.tsx should require authentication only for:

      - Create Private Room
      - Join Friend Room
      - Find Online Player

      Play with AI remains available to everyone.
    */

    // -------------------------------------------------
    // SHOW P-ARENA TRANSITION
    // -------------------------------------------------

    setLoadingGame(
      game.name
    );

    // -------------------------------------------------
    // ENTER GAME LOBBY
    // -------------------------------------------------

    window.setTimeout(
      () => {

        navigate(
          "/lobby",
          {
            state: {
              selectedGame:
                game.name,
            },
          }
        );

      },
      3000
    );
  };

  // ===================================================
  // UI
  // ===================================================

  return (
    <div className="app-background home-page">

      {/* =========================================== */}
      {/* AMBIENT BACKGROUND                          */}
      {/* =========================================== */}

      <div
        className="home-ambient-glow home-ambient-glow-one"
        aria-hidden="true"
      />

      <div
        className="home-ambient-glow home-ambient-glow-two"
        aria-hidden="true"
      />

      {/* =========================================== */}
      {/* P-ARENA LOADING                             */}
      {/* =========================================== */}

      <SplashScreen
        visible={
          loadingGame !== null
        }
        text={
          loadingGame
            ? `ENTERING ${loadingGame.toUpperCase()}`
            : "ENTERING THE ARENA"
        }
      />

      {/* =========================================== */}
      {/* MAIN CONTAINER                              */}
      {/* =========================================== */}

      <div className="home-container">

        {/* ========================================= */}
        {/* HEADER                                    */}
        {/* ========================================= */}

        <header className="home-header">

          {/* ======================================= */}
          {/* BRAND                                   */}
          {/* ======================================= */}

          <button
            type="button"
            className="home-brand"
            onClick={() =>
              navigate("/")
            }
            aria-label="P-ARENA Home"
          >
            <img
              src={pArenaLogo}
              alt="P-ARENA Game House"
              className="home-brand-logo"
              draggable={false}
            />
          </button>

          {/* ======================================= */}
          {/* AUTH AREA                               */}
          {/* ======================================= */}

          {isLoggedIn ? (

            <div className="auth-buttons">

              {/* PROFILE */}

              <button
                type="button"
                className="home-profile-button"
                onClick={() =>
                  navigate(
                    "/profile"
                  )
                }
              >

                <img
                  src={
                    getAvatarUrl(
                      currentUser?.avatar
                    )
                  }
                  alt=""
                  className="home-profile-avatar"
                  onError={(event) => {

                    event.currentTarget.onerror =
                      null;

                    event.currentTarget.src =
                      "/avatars/user.png";
                  }}
                />

                <span className="home-profile-name">
                  {
                    currentUser?.username ||
                    "Player"
                  }
                </span>

              </button>

              {/* NOTIFICATIONS */}

              <NotificationBell />

              {/* LOBBY */}

              <button
                type="button"
                className="home-nav-button"
                onClick={
                  openLobby
                }
              >
                <span
                  className="home-button-icon"
                  aria-hidden="true"
                >
                  ♠
                </span>

                Lobby
              </button>

              {/* ADMIN */}

              {currentUser?.role === "admin" && (

                <button
                  type="button"
                  className="home-nav-button home-admin-button"
                  onClick={() =>
                    navigate(
                      "/admin/support"
                    )
                  }
                >
                  <span
                    className="home-button-icon"
                    aria-hidden="true"
                  >
                    ♦
                  </span>

                  Admin
                </button>

              )}

              {/* ANALYTICS - ADMIN ONLY */}

              {currentUser?.role === "admin" && (

                <button
                  type="button"
                  className="home-nav-button home-admin-button"
                  onClick={() =>
                    navigate(
                      "/admin/analytics"
                    )
                  }
                >
                  <span
                    className="home-button-icon"
                    aria-hidden="true"
                  >
                    ♣
                  </span>

                  Analytics
                </button>

              )}

              {/* LOGOUT */}

              <button
                type="button"
                className="home-nav-button home-logout-button"
                onClick={
                  openLogoutConfirm
                }
              >
                Logout
              </button>

            </div>

          ) : (

            <div className="auth-buttons">

              <button
                type="button"
                className="home-nav-button"
                onClick={() =>
                  navigate(
                    "/login"
                  )
                }
              >
                Login
              </button>

              <button
                type="button"
                className="home-register-button"
                onClick={() =>
                  navigate(
                    "/register"
                  )
                }
              >
                Register
              </button>

            </div>

          )}

        </header>

        {/* ========================================= */}
        {/* HERO                                      */}
        {/* ========================================= */}

        <main className="home-main">

          <section className="home-hero">

            {/* ===================================== */}
            {/* HERO COPY                             */}
            {/* ===================================== */}

            <div className="home-hero-copy">

              <div className="home-eyebrow">

                <span className="home-eyebrow-line" />

                <span>
                  WELCOME TO P-ARENA
                </span>

              </div>

              <h1 className="home-hero-title">

                Pick a game.

                <span>
                  Enter the arena.
                </span>

              </h1>

              <p className="home-hero-description">
                Classic card games, competitive
                matches, and unforgettable moments.
                Play with friends, challenge online
                players, or sharpen your skills
                against AI.
              </p>

            </div>

            <div className="home-search-area">

            {/* =================================== */}
            {/* FASOOR COMMUNITY RATING             */}
            {/* =================================== */}

            <div
              className="home-rating-card"
              aria-label={
                overallRating &&
                overallRating.ratingCount > 0
                  ? `P-ARENA community rating ${overallRating.averageRating.toFixed(
                      1
                    )} out of 5 from ${overallRating.ratingCount} ratings`
                  : "P-ARENA community rating"
              }
            >

              <div className="home-rating-top">

                <div>

                  <div className="home-rating-eyebrow">
                    <span
                      className="home-rating-live-dot"
                      aria-hidden="true"
                    />

                    COMMUNITY RATING
                  </div>

                  <div className="home-rating-game">
                    P-ARENA
                  </div>

                </div>


                <div
                  className="home-rating-emblem"
                  aria-hidden="true"
                >
                  ♠
                </div>

              </div>


              {ratingLoading ? (

                <div className="home-rating-loading">

                  <span />

                  <span />

                  <span />

                </div>

              ) : overallRating &&
                overallRating.ratingCount > 0 ? (

                <div className="home-rating-content">

                  <div className="home-rating-score-row">

                    <div className="home-rating-score">
                      {
                        overallRating.averageRating.toFixed(
                          1
                        )
                      }
                    </div>

                    <div className="home-rating-out-of">
                      / 5
                    </div>

                  </div>


                  <div className="home-rating-stars">

                    {[1, 2, 3, 4, 5].map(
                      (star) => {

                        const fillPercentage =
                          Math.max(
                            0,
                            Math.min(
                              100,
                              (
                                overallRating.averageRating -
                                star +
                                1
                              ) * 100
                            )
                          );


                        return (

                          <span
                            key={star}
                            className="home-rating-star"
                            aria-hidden="true"
                          >

                            <span className="home-rating-star-empty">
                              ★
                            </span>

                            <span
                              className="home-rating-star-fill"
                              style={{
                                width:
                                  `${fillPercentage}%`,
                              }}
                            >
                              ★
                            </span>

                          </span>

                        );
                      }
                    )}

                  </div>


                  <div className="home-rating-count">

                    Based on{" "}

                    <strong>
                      {
                        overallRating.ratingCount.toLocaleString()
                      }
                    </strong>

                    {" "}

                    {
                      overallRating.ratingCount === 1
                        ? "player rating"
                        : "player ratings"
                    }

                  </div>

                </div>

              ) : (

                <div className="home-rating-empty">

                  <div className="home-rating-empty-stars">
                    ★ ★ ★ ★ ★
                  </div>

                  <span>
                    Be among the first to rate a P-ARENA game
                  </span>

                </div>

              )}

            </div>


            {/* =================================== */}
            {/* SEARCH                              */}
            {/* =================================== */}

            <label
              htmlFor="game-search"
              className="home-search-label"
            >
              FIND YOUR GAME
            </label>

              <div className="search-box">

                <span
                  className="search-icon"
                  aria-hidden="true"
                >
                  ⌕
                </span>

                <input
                  id="game-search"
                  type="search"
                  placeholder="Search games..."
                  value={
                    search
                  }
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                  autoComplete="off"
                />

                {search && (

                  <button
                    type="button"
                    className="search-clear-button"
                    aria-label="Clear search"
                    onClick={() =>
                      setSearch("")
                    }
                  >
                    ×
                  </button>

                )}

              </div>

            </div>

          </section>

          {/* ======================================= */}
          {/* GAME LIBRARY                            */}
          {/* ======================================= */}

          <section className="games-section">

            {/* ===================================== */}
            {/* SECTION HEADER                        */}
            {/* ===================================== */}

            <div className="section-heading">

              <div>

                <span className="section-eyebrow">
                  GAME LIBRARY
                </span>

                <h2 className="section-title">
                  {
                    search.trim()
                      ? "Search Results"
                      : "Featured Games"
                  }
                </h2>

              </div>

              <div className="games-count">
                {
                  displayedGames.length
                }

                {
                  displayedGames.length === 1
                    ? " Game"
                    : " Games"
                }
              </div>

            </div>

            {/* ===================================== */}
            {/* GAMES                                 */}
            {/* ===================================== */}

            {displayedGames.length > 0 ? (

              <div className="games-grid">

                {displayedGames.map(
                  (game) => (

                    <GameCard
                      key={
                        game.id
                      }
                      game={{
                        ...game,

                        rating:
                          gameRatings[
                            game.name
                              .trim()
                              .toLowerCase()
                              .replace(
                                /\s+/g,
                                "-"
                              )
                          ],
                      }}
                      onClick={() =>
                        openGame(
                          game
                        )
                      }
                    />

                  )
                )}

              </div>

            ) : (

              /* =================================== */
              /* EMPTY SEARCH                        */
              /* =================================== */

              <div className="home-empty-state">

                <div
                  className="home-empty-icon"
                  aria-hidden="true"
                >
                  ♣
                </div>

                <h3>
                  No games found
                </h3>

                <p>
                  We couldn't find a game matching
                  "{search}".
                </p>

                <button
                  type="button"
                  onClick={() =>
                    setSearch("")
                  }
                >
                  View Featured Games
                </button>

              </div>

            )}

          </section>

        </main>

      </div>

      {/* =========================================== */}
      {/* FOOTER                                      */}
      {/* =========================================== */}

      <Footer />

      {/* =========================================== */}
      {/* LOGOUT CONFIRMATION                         */}
      {/* =========================================== */}

      <FasoorModal
        open={
          showLogoutConfirm
        }
        type="warning"
        title="Log Out?"
        message="Are you sure you want to log out?"
        description="You will need to log in again to access your friends and online multiplayer."
        confirmText="Logout"
        cancelText="Stay Logged In"
        showCancel
        onCancel={
          cancelLogout
        }
        onConfirm={
          confirmLogout
        }
      />

    </div>
  );
};

export default Home;